/**
 * Pie Chart Widget Component
 * Displays data as a pie/donut chart using Recharts
 */

import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartData, ChartConfig } from '../../types';

interface ChartPieWidgetProps {
  data: ChartData;
  config?: ChartConfig;
  loading?: boolean;
  title?: string;
  donut?: boolean;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#eab308', // yellow
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

export function ChartPieWidget({
  data,
  config,
  loading,
  title,
  donut = false,
}: ChartPieWidgetProps) {
  // Transform data for pie chart
  const chartData = useMemo(() => {
    if (!data.datasets || data.datasets.length === 0) {
      return [];
    }

    // Use first dataset
    const dataset = data.datasets[0];

    if (data.labels && Array.isArray(dataset.data)) {
      return data.labels.map((label, idx) => ({
        name: label,
        value: dataset.data[idx] as number,
      }));
    }

    // If data is already in the right format
    if (Array.isArray(dataset.data) && typeof dataset.data[0] === 'object') {
      return dataset.data.map((item: unknown) => {
        const obj = item as Record<string, unknown>;
        return {
          name: obj.name || obj.label || 'Unknown',
          value: obj.value || obj.count || 0,
        };
      });
    }

    return [];
  }, [data]);

  const colors = config?.colors || DEFAULT_COLORS;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="animate-pulse w-32 h-32 bg-gray-100 rounded-full" />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-gray-500">
        <p>No data available</p>
      </div>
    );
  }

  // Calculate total for percentage display
  const total = chartData.reduce((sum, item) => sum + (item.value as number), 0);

  return (
    <div className="flex flex-col h-full p-4">
      {title && (
        <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
      )}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={donut ? '60%' : 0}
              outerRadius="80%"
              paddingAngle={2}
              dataKey="value"
              label={({ name, value }) =>
                `${name}: ${((value as number / total) * 100).toFixed(1)}%`
              }
              labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
            >
              {chartData.map((_, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={colors[idx % colors.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [`${numValue} (${((numValue / total) * 100).toFixed(1)}%)`, 'Value'];
              }}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={10}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
